import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  FiDownload,
  FiTrash2,
  FiFile,
  FiFileText,
  FiMic,
  FiSearch,
  FiCalendar,
  FiUser,
  FiHardDrive,
  FiCloud,
  FiEye,
} from "react-icons/fi";
import { fileAPI } from "../../api/apiHelper.js";


// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`file-tabpanel-${index}`}
      aria-labelledby={`file-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// File card component
function FileCard({ file, onDelete, onDownload }) {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [llmDialogOpen, setLlmDialogOpen] = useState(false);

  const getFileIcon = (fileType, type) => {
    if (type === "audioFile" || fileType?.startsWith("audio/")) {
      return <FiMic size={24} style={{ color: "#396afc" }} />;
    }
    return <FiFileText size={24} style={{ color: "#43cea2" }} />;
  };

  const getStorageIcon = (storageType) => {
    return storageType === "gcs" ? (
      <FiCloud size={16} style={{ color: "#4285f4" }} />
    ) : (
      <FiHardDrive size={16} style={{ color: "#34a853" }} />
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDelete = () => {
    onDelete(file._id);
    setDeleteDialogOpen(false);
  };

  // Helper to pretty print LLM response
  const renderLlmResponse = () => {
    if (!file.llmResponse) {
      let msg = "No LLM response found.";
      if (file.llmStatus === "processing") msg = "AI is processing this file...";
      if (file.llmStatus === "pending") msg = "AI processing not started yet.";
      if (file.llmStatus === "failed") msg = "AI processing failed for this file.";
      return <Typography color="warning.main">{msg}</Typography>;
    }
    let clean = file.llmResponse.trim();
    // Remove triple backticks and optional 'json' and newlines at start
    clean = clean.replace(/^```json\s*|^```\s*/i, "");
    // Remove triple backticks at end (with or without newline)
    clean = clean.replace(/\s*```\s*$/i, "");
    clean = clean.trim();
    // Fix for value_prop_score: .75 to 0.75 (invalid JSON)
    clean = clean.replace(/("value_prop_score"\s*:\s*)\.(\d+)/g, '$10.$2');
    let parsed;
    try {
      parsed = JSON.parse(clean);
    } catch {
      return (
        <Typography color="error">Invalid LLM response format.</Typography>
      );
    }
    return (
      <Box>
        {parsed.product_analysis && (
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              Product Analysis
            </Typography>
            <Typography variant="body2" mb={1}>
              <b>Alignment Summary:</b> {parsed.product_analysis.alignment_summary}
            </Typography>
            <Typography variant="body2" mb={1}>
              <b>Value Prop Score:</b> {parsed.product_analysis.value_prop_score}
            </Typography>
            <Box mb={1}>
              <Typography variant="body2" fontWeight={600}>
                Features Discussed:
              </Typography>
              {parsed.product_analysis.features_discussed?.map((f, idx) => (
                <Box key={idx} ml={2} mb={1}>
                  <Typography variant="body2">
                    <b>{f.feature_name}</b> ({f.mentioned_time})
                  </Typography>
                  <Typography variant="caption">
                    Customer: {f.customer_reaction} | {f.context}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {parsed.objection_tracker && parsed.objection_tracker.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              Objection Tracker
            </Typography>
            {parsed.objection_tracker.map((o, idx) => (
              <Box key={idx} ml={2} mb={1}>
                <Typography variant="body2">
                  <b>Objection:</b> {o.objection}
                </Typography>
                <Typography variant="caption">
                  At {o.timestamp} | Handled Well: {o.handled_well ? "Yes" : "No"}
                </Typography>
                <Typography variant="caption" display="block">
                  {o.response_summary}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {parsed.competitive_landscape && (
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              Competitive Landscape
            </Typography>
            <Typography variant="body2">
              <b>Competitors Mentioned:</b>{" "}
              {parsed.competitive_landscape.competitors_mentioned?.join(", ")}
            </Typography>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Our Strengths Highlighted:
              </Typography>
              {parsed.competitive_landscape.our_strengths_highlighted?.map(
                (s, idx) => (
                  <Typography key={idx} variant="caption" display="block">
                    {s.point} ({s.mentioned_time})
                  </Typography>
                )
              )}
            </Box>
          </Box>
        )}
        {/* Add more sections as needed */}
      </Box>
    );
  };

  return (
    <>
      <Card
        sx={{
          mb: 2,
          border: file.type === 'preCallInsights' ? "2px solid #7c3aed" : "1px solid #e0e7ef",
          borderRadius: 2,
          backgroundColor: file.type === 'preCallInsights' ? "#faf5ff" : "white",
          "&:hover": {
            boxShadow: file.type === 'preCallInsights' ? "0 6px 16px rgba(124, 58, 237, 0.2)" : "0 4px 12px rgba(0,0,0,0.1)",
            borderColor: file.type === 'preCallInsights' ? "#7c3aed" : "#396afc",
            transform: file.type === 'preCallInsights' ? "translateY(-2px)" : "none"
          },
          transition: "all 0.2s ease-in-out",
          cursor: file.type === 'preCallInsights' ? "pointer" : "default"
        }}
        onClick={() => file.type === 'preCallInsights' && navigate('/home/pre-call-insights')}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={2} flex={1}>
              {getFileIcon(file.fileType, file.type)}
              <Box flex={1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: file.type === 'preCallInsights' ? "#7c3aed" : "#2d3748",
                    mb: 0.5,
                    wordBreak: "break-word",
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  {file.type === 'preCallInsights' && '💡 '}
                  {file.originalFileName}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Chip
                    label={file.type === 'preCallInsights' ? 'AI Insights' : file.type}
                    size="small"
                    sx={{
                      backgroundColor:
                        file.type === "audioFile" ? "#e3f2fd" : 
                        file.type === 'preCallInsights' ? "#f3e8ff" : 
                        "#e8f5e8",
                      color: file.type === "audioFile" ? "#1976d2" : 
                        file.type === 'preCallInsights' ? "#7c3aed" :
                        "#2e7d32",
                      fontSize: 11,
                      height: 20,
                      fontWeight: file.type === 'preCallInsights' ? 600 : 400
                    }}
                  />
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {getStorageIcon(file.storageType)}
                    <Typography variant="caption" color="#7b7b93">
                      {file.storageType === "gcs" ? "Cloud" : "Local"}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    sx={{
                      ml: 1,
                      color:
                        file.llmStatus === "completed" ? "#7c3aed" : "#bdbdbd",
                    }}
                    title="View AI Insights"
                    onClick={() => setLlmDialogOpen(true)}
                  >
                    <span role="img" aria-label="AI">
                      🤖
                    </span>
                  </IconButton>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="caption" color="#7b7b93">
                    {formatFileSize(file.fileSize)}
                  </Typography>
                  <Typography variant="caption" color="#7b7b93">
                    {formatDate(file.uploadedAt)}
                  </Typography>
                  {file.userId?.name && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <FiUser size={12} color="#7b7b93" />
                      <Typography variant="caption" color="#7b7b93">
                        {file.userId.name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton
                onClick={() => onDownload(file)}
                sx={{
                  color: file.type === 'preCallInsights' ? "#7c3aed" : "#396afc",
                  "&:hover": { backgroundColor: file.type === 'preCallInsights' ? "#f3e8ff" : "#e3f2fd" },
                }}
                title={file.type === 'preCallInsights' ? "View Insights" : "Download file"}
              >
                {file.type === 'preCallInsights' ? <FiEye size={18} /> : <FiDownload size={18} />}
              </IconButton>
              <IconButton
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  color: "#e53e3e",
                  "&:hover": { backgroundColor: "#fed7d7" },
                }}
                title="Delete file"
              >
                <FiTrash2 size={18} />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{file.originalFileName}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* LLM Response Dialog */}
      <Dialog
        open={llmDialogOpen}
        onClose={() => setLlmDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>AI Insights for {file.originalFileName}</DialogTitle>
        <DialogContent dividers>{renderLlmResponse()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setLlmDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function FileManager() {
  const [activeTab, setActiveTab] = useState(0);
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Get organization ID from user data (you might need to adjust this based on your auth setup)
  const getOrganizationId = () => {
    // This should be retrieved from your auth context or user data
    // For now, we'll use a placeholder - you'll need to implement this
    const userData = localStorage.getItem("orgId");
    return userData;
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const loadFiles = async () => {
    try {
      setLoading(true);
      const organizationId = getOrganizationId();

      if (!organizationId) {
        showSnackbar(
          "Organization ID not found. Please log in again.",
          "error"
        );
        return;
      }

      const response = await fileAPI.getOrganizationFiles(organizationId);
      console.log('FileManager - Full response:', response);
      console.log('FileManager - response.files:', response.files);
      console.log('FileManager - response.data:', response.data);
      
      const filesArray = response.files || response.data?.files || [];
      console.log('✅ Files array to set:', filesArray);
      console.log('✅ Files count:', filesArray.length);
      
      setFiles(filesArray);
    } catch (error) {
      console.error("Error loading files:", error);
      showSnackbar(`Failed to load files: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await fileAPI.deleteFile(fileId);
      setFiles((prevFiles) => prevFiles.filter((file) => file._id !== fileId));
      showSnackbar("File deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting file:", error);
      showSnackbar(`Failed to delete file: ${error.message}`, "error");
    }
  };

  const navigate = useNavigate();

  const handleDownloadFile = async (file) => {
    try {
      // Special handling for Pre-Call Insights
      if (file.type === 'preCallInsights') {
        navigate('/home/pre-call-insights');
        showSnackbar('Opening Pre-Call Insights...', 'info');
        return;
      }

      if (file.downloadUrl) {
        // If we have a download URL, open it directly
        window.open(file.downloadUrl, "_blank");
      } else if (file.publicUrl) {
        // Fallback to public URL
        window.open(file.publicUrl, "_blank");
      } else {
        showSnackbar("Download URL not available", "error");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      showSnackbar(`Failed to download file: ${error.message}`, "error");
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filterFiles = () => {
    let filtered = files;

    // Filter by tab (file type)
    if (activeTab === 1) {
      filtered = filtered.filter((file) => file.type === "audioFile");
    } else if (activeTab === 2) {
      filtered = filtered.filter((file) => file.type === "companyDocs");
    } else if (activeTab >= 3) {
      const competitorTypes = [
        "competitor1",
        "competitor2",
        "competitor3",
        "competitor4",
        "competitor5",
      ];
      const competitorIndex = activeTab - 3;
      if (competitorIndex < competitorTypes.length) {
        filtered = filtered.filter(
          (file) => file.type === competitorTypes[competitorIndex]
        );
      }
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((file) =>
        file.originalFileName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredFiles(filtered);
  };

  useEffect(() => {
    loadFiles();
  }, []);

  useEffect(() => {
    filterFiles();
  }, [files, activeTab, searchTerm]);

  const getTabCount = (type) => {
    if (type === "all") return files.length;
    if (type === "audioFile")
      return files.filter((f) => f.type === "audioFile").length;
    if (type === "companyDocs")
      return files.filter((f) => f.type === "companyDocs").length;
    const competitorTypes = [
      "competitor1",
      "competitor2",
      "competitor3",
      "competitor4",
      "competitor5",
    ];
    const competitorIndex = competitorTypes.indexOf(type);
    if (competitorIndex >= 0) {
      return files.filter((f) => f.type === type).length;
    }
    return 0;
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        margin: "0 auto",
        padding: "0 0",
        background: "#fff",
        borderRadius: 3,
        padding: 0,
        minHeight: 600,
      }}
    >
      {/* Header */}
      {/* <Box display="flex" alignItems="center" justifyContent="flex-end" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            placeholder="Search files..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch style={{ color: "#7b7b93" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#f7fafc",
              },
            }}
          />
          <Button
            variant="outlined"
            onClick={loadFiles}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} /> : <FiCalendar />
            }
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              borderColor: "#396afc",
              color: "#396afc",
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box> */}

      {/* Tabs */}
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2, paddingTop: "0" }}
      >
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable">
          <Tab
            label={`All Files (${getTabCount("all")})`}
            icon={<FiFile size={16} />}
            iconPosition="start"
          />
          <Tab
            label={`Audio Files (${getTabCount("audioFile")})`}
            icon={<FiMic size={16} />}
            iconPosition="start"
          />
          <Tab
            label={`Company Docs (${getTabCount("companyDocs")})`}
            icon={<FiFileText size={16} />}
            iconPosition="start"
          />
          <Tab
            label={`Competitor 1 (${getTabCount("competitor1")})`}
            icon={<FiFileText size={16} />}
            iconPosition="start"
          />
          <Tab
            label={`Competitor 2 (${getTabCount("competitor2")})`}
            icon={<FiFileText size={16} />}
            iconPosition="start"
          />
          <Tab
            label={`Competitor 3 (${getTabCount("competitor3")})`}
            icon={<FiFileText size={16} />}
            iconPosition="start"
          />
          <Tab
            label={`Competitor 4 (${getTabCount("competitor4")})`}
            icon={<FiFileText size={16} />}
            iconPosition="start"
          />
          <Tab
            label={`Competitor 5 (${getTabCount("competitor5")})`}
            icon={<FiFileText size={16} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <FileList
          files={filteredFiles}
          loading={loading}
          onDelete={handleDeleteFile}
          onDownload={handleDownloadFile}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <FileList
          files={filteredFiles}
          loading={loading}
          onDelete={handleDeleteFile}
          onDownload={handleDownloadFile}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <FileList
          files={filteredFiles}
          loading={loading}
          onDelete={handleDeleteFile}
          onDownload={handleDownloadFile}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <FileList
          files={filteredFiles}
          loading={loading}
          onDelete={handleDeleteFile}
          onDownload={handleDownloadFile}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <FileList
          files={filteredFiles}
          loading={loading}
          onDelete={handleDeleteFile}
          onDownload={handleDownloadFile}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={5}>
        <FileList
          files={filteredFiles}
          loading={loading}
          onDelete={handleDeleteFile}
          onDownload={handleDownloadFile}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={6}>
        <FileList
          files={filteredFiles}
          loading={loading}
          onDelete={handleDeleteFile}
          onDownload={handleDownloadFile}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={7}>
        <FileList
          files={filteredFiles}
          loading={loading}
          onDelete={handleDeleteFile}
          onDownload={handleDownloadFile}
        />
      </TabPanel>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// File List Component
function FileList({ files, loading, onDelete, onDownload }) {
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={200}
        color="#7b7b93"
      >
        <FiFile size={48} style={{ marginBottom: 16 }} />
        <Typography variant="h6" color="#7b7b93">
          No files found
        </Typography>
        <Typography variant="body2" color="#7b7b93">
          Upload some files to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {files.map((file) => (
        <FileCard
          key={file._id}
          file={file}
          onDelete={onDelete}
          onDownload={onDownload}
        />
      ))}
    </Box>
  );
}
